import "components/calendar/calendar.scss"
import { Fragment, h } from "preact";
import { RouterProps } from "preact-router";
import { Button, Container, ToastViewer } from "uitoolkit";
import { usePageTitle } from "hooks";
import { Sidebar } from "components/sidebar";
import { buildHalfYear, buildMonthModel, buildWeekModel, dayNames, getWeek, monthNames, type DayModel, type MonthModel, type WeekModel } from "components/calendar/common";
import { useState } from "preact/hooks";
import { SingleDayCalendar } from "components/calendar/singleDayCalendar";
import { SingleWeekCalendar } from "components/calendar/multiDayCalendar";
import { SingleMonthCalendar } from "components/calendar/singleMonthCalendar";
import { MultiMonthCalendar } from "components/calendar/multiMonthCalendar";

type Selection =
    | {
        type: "day",
        day: DayModel,
    } | {
        type: "workweek",
        week: WeekModel,
    } | {
        type: "week",
        week: WeekModel,
    } | {
        type: "month",
        month: MonthModel,
    } | {
        type: "halfyear",
        months: MonthModel[],
    };

export function CalendarPage(props: {} & RouterProps) {
    usePageTitle("Calendar");
    const [selection, setSelection] = useState<Selection>({ type: "halfyear", months: buildHalfYear(new Date()) });

    const onDaySelected = (date: Date) => setSelection({ type: "day", day: { date } });
    const onWeekSelected = (firstDayInWeek: Date) => setSelection({ type: "week", week: buildWeekModel(firstDayInWeek) });
    const onMonthSelected = (month: MonthModel) => setSelection({ type: "month", month });

    const onGoToday = () => {
        switch (selection?.type) {
            case "day":
                setSelection({ type: "day", day: { date: new Date() } });
                break;
            case "workweek":
                // todo: handle if today is a saturday or sunday
                setSelection({ type: "workweek", week: buildWeekModel(new Date(), 5) });
                break;
            case "week":
                setSelection({ type: "week", week: buildWeekModel(new Date()) });
                break;
            case "month":
                {
                    const date = new Date();
                    setSelection({ type: "month", month: buildMonthModel(date.getFullYear(), date.getMonth()) });
                }
                break;
            case "halfyear":
                setSelection({ type: "halfyear", months: buildHalfYear(new Date()) });
                break;
        }
    };

    const step = (direction: 1 | -1) => {
        switch (selection?.type) {
            case "day":
                {
                    const date = new Date(selection.day.date.valueOf());
                    date.setDate(date.getDate() + direction);
                    setSelection({ type: "day", day: { date } });
                }
                break;
            case "workweek":
                {
                    const date = new Date(selection.week.firstDate.valueOf());
                    date.setDate(date.getDate() + (direction * 7));
                    setSelection({ type: "workweek", week: buildWeekModel(date, 5) });
                }
                break;
            case "week":
                {
                    const date = new Date(selection.week.firstDate.valueOf());
                    date.setDate(date.getDate() + (direction * 7));
                    setSelection({ type: "week", week: buildWeekModel(date) });
                }
                break;
            case "month":
                {
                    const date = new Date(selection.month.firstDate.valueOf());
                    date.setMonth(date.getMonth() + direction);
                    setSelection({ type: "month", month: buildMonthModel(date.getFullYear(), date.getMonth()) });
                }
                break;
            case "halfyear":
                {
                    const date = new Date(selection.months[0].firstDate.valueOf());
                    date.setMonth(date.getMonth() + (direction * 6));
                    setSelection({ type: "halfyear", months: buildHalfYear(date) });
                }
                break;
        }
    }

    const previous = () => step(-1);
    const next = () => step(1);

    const onViewChanged = (type: Selection["type"]) => {
        if (type == selection?.type) return;
        switch (type) {
            case "halfyear":
                switch (selection?.type) {
                    case "month":
                        setSelection({ type: "halfyear", months: buildHalfYear(selection.month.firstDate) });
                        break;
                    case "workweek":
                    case "week":
                        setSelection({ type: "halfyear", months: buildHalfYear(selection.week.firstDate) });
                        break;
                    case "day":
                        setSelection({ type: "halfyear", months: buildHalfYear(selection.day.date) });
                        break;
                }
                break;
            case "month":
                switch (selection?.type) {
                    case "halfyear":
                        setSelection({ type: "month", month: buildMonthModel(selection.months[0].firstDate.getFullYear(), selection.months[0].firstDate.getMonth()) }); // todo: maybe pick better date?
                        break;
                    case "workweek":
                    case "week":
                        setSelection({ type: "month", month: buildMonthModel(selection.week.firstDate.getFullYear(), selection.week.firstDate.getMonth()) });
                        break;
                    case "day":
                        setSelection({ type: "month", month: buildMonthModel(selection.day.date.getFullYear(), selection.day.date.getMonth()) });
                        break;
                }
                break;
            case "workweek":
                switch (selection?.type) {
                    case "halfyear":
                        setSelection({ type: "workweek", week: buildWeekModel(selection.months[0].firstDate, 5) }); // todo: maybe pick better date?
                        break;
                    case "month":
                        setSelection({ type: "workweek", week: buildWeekModel(selection.month.firstDate, 5) }); // todo: maybe pick better date?
                        break;
                    case "week":
                        setSelection({ type: "workweek", week: buildWeekModel(selection.week.firstDate, 5) }); // todo: maybe pick better date?
                        break;
                    case "day":
                        setSelection({ type: "workweek", week: buildWeekModel(selection.day.date, 5) });
                        break;
                }
                break;
            case "week":
                switch (selection?.type) {
                    case "halfyear":
                        setSelection({ type: "week", week: buildWeekModel(selection.months[0].firstDate) }); // todo: maybe pick better date?
                        break;
                    case "month":
                        setSelection({ type: "week", week: buildWeekModel(selection.month.firstDate) }); // todo: maybe pick better date?
                        break;
                    case "workweek":
                        setSelection({ type: "week", week: buildWeekModel(selection.week.firstDate) });
                        break;
                    case "day":
                        setSelection({ type: "week", week: buildWeekModel(selection.day.date) });
                        break;
                }
                break;
            case "day":
                switch (selection?.type) {
                    case "halfyear":
                        setSelection({ type: "day", day: { date: selection.months[0].firstDate } });  // todo: maybe pick better date?
                        break;
                    case "month":
                        setSelection({ type: "day", day: { date: selection.month.firstDate } });  // todo: maybe pick better date?
                        break;
                    case "workweek":
                    case "week":
                        setSelection({ type: "day", day: { date: selection.week.firstDate } });  // todo: maybe pick better date?
                        break;
                }
                break;
        }
    };

    const getTitle = () => {
        switch (selection?.type) {
            case "day":
                return dayNames[selection.day.date.getDay()] + " " + (selection.day.date.getDate()) + " " + monthNames[selection.day.date.getMonth()] + " " + selection.day.date.getFullYear();
            case "workweek":
                return "workweek " + getWeek(selection.week.firstDate) + ", " + selection.week.firstDate.getFullYear();
            case "week":
                return "week " + getWeek(selection.week.firstDate) + ", " + selection.week.firstDate.getFullYear();
            case "month":
                return monthNames[selection.month.firstDate.getMonth()] + " " + selection.month.firstDate.getFullYear();
            case "halfyear":
                return (selection.months[0].firstDate.getMonth() == 0 ? "H1 " : "H2 ") + selection.months[0].firstDate.getFullYear()
        }
        return "";
    };

    return (
        <Fragment>
            <Container class="row grow maxheight-100vh overflow-y-hidden">
                <Sidebar page="calendar" />
                <Container class="flex-column overflow-y-auto grow gap-8" style={{ padding: "4px" }}>
                    <Container class="component-form flex-row gap-8 noprint">
                        <Button onClick={onGoToday}>Today</Button>
                        <Button onClick={previous}>&lt;</Button>
                        <Button onClick={next}>&gt;</Button>
                        <select onChange={e => onViewChanged(e.currentTarget.value as any)}>
                            <option value="halfyear" selected={selection?.type == "halfyear"}>Half year</option>
                            <option value="month" selected={selection?.type == "month"}>Month</option>
                            <option value="week" selected={selection?.type == "week"}>Week</option>
                            <option value="workweek" selected={selection?.type == "workweek"}>Work week</option>
                            <option value="day" selected={selection?.type == "day"}>Day</option>
                        </select>
                        <h2>{getTitle()}</h2>
                    </Container>
                    <Container class="flex-row grow">
                        {
                            selection?.type == "day" &&
                            <SingleDayCalendar model={selection.day} />
                        }
                        {
                            selection?.type == "workweek" &&
                            <SingleWeekCalendar week={selection.week} onDaySelected={onDaySelected} />
                        }
                        {
                            selection?.type == "week" &&
                            <SingleWeekCalendar week={selection.week} onDaySelected={onDaySelected} />
                        }
                        {
                            selection?.type == "month" &&
                            <SingleMonthCalendar month={selection.month} onDaySelected={onDaySelected} />
                        }
                        {
                            selection?.type == "halfyear" &&
                            <MultiMonthCalendar months={selection.months} onDaySelected={onDaySelected} onWeekSelected={onWeekSelected} onMonthSelected={onMonthSelected} />
                        }
                    </Container>
                </Container>
            </Container>
            <ToastViewer />
        </Fragment>
    );
}
